/**
 * O resto das informações de um pagamento — o que não cabe na linha da tabela.
 *
 * A tabela carrega o que se compara entre pagamentos (valor, meio, tipo, pagador, conta, estado);
 * aqui ficam os identificadores e o rastro de quem registrou, que só interessam quando alguém já
 * escolheu uma linha para investigar.
 */
import { useState } from 'react';
import { FaTimes, FaFilePdf } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { PaymentRow, getPaymentReceiptUrl } from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatTaxId } from '../../../../utils/taxIdUtils';
import { STATUS_META } from '../PaymentsPanel';
import NotesList from '../../../molecules/NotesList';

/**
 * Nome do comprovante como link de download.
 *
 * O clique busca um link temporário no backend e ABRE (`window.open`), em vez de baixar por
 * fetch: o bucket não tem CORS. Um `<a href>` direto também não serviria — a rota exige
 * cabeçalho de autenticação, que navegação não manda.
 */
const ReceiptLink: React.FC<{ payment: PaymentRow }> = ({ payment }) => {
    const [loading, setLoading] = useState(false);
    if (!payment.fileNameReceipt) return null;

    const open = async () => {
        setLoading(true);
        try {
            const url = await getPaymentReceiptUrl(payment.paymentId);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e: any) {
            toast.error(e?.message || 'Falha ao obter o comprovante');
        } finally {
            setLoading(false);
        }
    };

    return (
        <button type="button" onClick={open} disabled={loading}
            title="Baixar o comprovante de pagamento"
            className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-60 dark:text-blue-400">
            <FaFilePdf />
            <span className="break-all">{payment.fileNameReceipt}</span>
            {loading && <span className="text-xs text-gray-400">(abrindo...)</span>}
        </button>
    );
};

const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-1.5 border-b dark:border-slate-700 last:border-0">
        <span className="text-gray-500 dark:text-gray-400">{label}</span>
        <span className="text-right font-medium break-all">{value ?? '—'}</span>
    </div>
);

/**
 * A observação de quem registrou, em BLOCO e não numa linha de `Row`.
 *
 * É texto livre e pode ter várias linhas: alinhado à direita como os demais valores, uma frase
 * longa vira uma escada estreita colada na borda. `whitespace-pre-wrap` preserva as quebras que
 * o usuário digitou — o texto foi escrito para ser relido, não normalizado.
 *
 * Aparece mesmo vazia: quem procura o porquê de um pagamento precisa saber que o campo existe e
 * que ninguém escreveu nada, e não ficar em dúvida se a tela deixou de mostrar.
 */
const NoteBlock: React.FC<{ notes: string[] }> = ({ notes }) => (
    <div className="pt-3 mt-2 border-t dark:border-slate-700">
        <span className="text-gray-500 dark:text-gray-400">Observações</span>
        <div className="mt-1">
            <NotesList notes={notes} label="Observações do pagamento" variant="block" emptyText="—" />
        </div>
    </div>
);

/** Instante em UTC → leitura local, que é o dia que a tela filtra. */
const instante = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString('pt-BR') : null;

interface Props {
    payment: PaymentRow;
    /**
     * Camada do dialog. Default `z-50`, que serve quando ele abre a partir de uma tela.
     *
     * Aberto de DENTRO de outro modal, precisa subir: o modal de marcação é `z-[60]`, e com o
     * default este dialog abria atrás dele — o usuário só via a tela escurecer.
     */
    zIndexClass?: string;
    onClose: () => void;
}

const PaymentInfoModal: React.FC<Props> = ({ payment, zIndexClass = 'z-50', onClose }) => (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${zIndexClass} p-4`} onClick={onClose}
        role="dialog" aria-modal="true" aria-label="Informações do pagamento">
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                <h3 className="text-lg font-semibold">Informações do pagamento</h3>
                <button onClick={onClose} aria-label="Fechar"
                    className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            <div className="px-6 py-4 text-sm">
                <Row label="Identificador" value={payment.paymentId} />
                <Row label="Estado" value={STATUS_META[payment.status].label} />
                <Row label="Valor" value={payment.amount != null ? formatBRL(payment.amount) : null} />
                <Row label="Data do pagamento" value={instante(payment.paymentDate)} />
                <Row label="Pagador" value={payment.payer?.name} />
                <Row label="Documento"
                    value={payment.payer?.documentNumber ? formatTaxId(payment.payer.documentNumber) : null} />
                <Row label="Conta de recebimento" value={payment.receivingBankAccount?.exibitionName} />
                <Row label="Faturas sinalizadas" value={payment.linkedInvoicesCount} />
                <Row label="Valor sinalizado" value={formatBRL(payment.markedAmount)} />
                <Row label="Registrado por" value={payment.userResponsible} />
                <Row label="Recibo" value={<ReceiptLink payment={payment} />} />
                <Row label="Criado em" value={instante(payment.createdAt)} />
                <NoteBlock notes={payment.notesList ?? (payment.notes ? [payment.notes] : [])} />
            </div>
        </div>
    </div>
);

export default PaymentInfoModal;
